/**
 * PII guard for outbound analytics properties (AC-711, Acceptance Criterion 9).
 *
 * Mixpanel properties are effectively immutable once received: a property sent
 * by mistake cannot be un-sent, only deleted by support. A casino handles
 * exactly the categories of data that must never reach a third-party analytics
 * vendor — emails, phone numbers, KYC documents, card numbers, crypto wallet
 * addresses, passwords, OTP codes.
 *
 * So this is a deny-list applied at the boundary, in `mixpanel.ts`, to every
 * event, super-property and profile trait. It is defence in depth, not a
 * licence to be careless at the call site: never pass PII in the first place.
 *
 * The match is on the property KEY (substring, case-insensitive), because that
 * is what stays stable when an upstream shape changes. If a payment provider
 * adds `customer_email` to a response we spread into an event, this catches it.
 *
 * Deliberately allowed: `anonymous_id`, `user_id`, `player_id` — pseudonymous
 * identifiers that are the whole point of the integration. Hence the allow-list
 * escape hatch below, which is checked first.
 */

const ALLOWED_KEYS = new Set([
    "user_id",
    "player_id",
    "anonymous_id",
    "session_id",
    "click_id",
    "landing_id",
    "campaign_id",
]);

/**
 * Substrings that mark a key as PII. Keep this list current — it is the
 * mitigation the compliance story depends on.
 *
 * These are deliberately specific rather than broad. A bare `code` would redact
 * `currency_code` and `error_code`; a bare `pin` would redact `free_spins`; a
 * bare `pan` would redact `company`. A false positive here is not harmless — it
 * silently guts a real funnel property and the loss is invisible in Mixpanel.
 * Match the sensitive key, not a fragment that happens to appear inside it.
 */
const DENIED_KEY_PATTERNS = [
    "email",
    "phone",
    "msisdn",
    "password",
    "passwd",
    "secret",
    "token",
    "otp",
    // Verification/one-time codes only — never `currency_code` / `error_code` / `partner_code`.
    "verification_code",
    "confirmation_code",
    "security_code",
    "sms_code",
    "auth_code",
    "pin_code",
    "cvv",
    "card_number",
    "cardnumber",
    "card_pan",
    "iban",
    "swift",
    "account_number",
    // Covers wallet_address, email_address, ip_address, billing/street address.
    "address",
    "first_name",
    "last_name",
    "full_name",
    "middle_name",
    "username",
    "birth",
    "dob",
    "passport",
    "document",
    "ssn",
    "tax_id",
];

export const REDACTED = "[redacted]";

const isDenied = (key: string): boolean => {
    const normalized = key.toLowerCase();
    if (ALLOWED_KEYS.has(normalized)) return false;
    return DENIED_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
};

/**
 * Strips PII-shaped keys from a property bag. Nested objects are walked; the
 * offending values are replaced with `[redacted]` rather than dropped, so a
 * mistake is visible in Mixpanel (and therefore fixable) instead of silent.
 *
 * Warns loudly in development so it is caught in review, not in production.
 */
export const sanitizeProperties = (
    properties: Record<string, unknown> = {},
): Record<string, unknown> => {
    const sanitized: Record<string, unknown> = {};
    const redactedKeys: string[] = [];

    for (const [key, value] of Object.entries(properties)) {
        if (isDenied(key)) {
            sanitized[key] = REDACTED;
            redactedKeys.push(key);
            continue;
        }

        if (value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date)) {
            sanitized[key] = sanitizeProperties(value as Record<string, unknown>);
            continue;
        }

        sanitized[key] = value;
    }

    if (redactedKeys.length && process.env.NODE_ENV !== "production") {
        console.warn(
            `[mixpanel] PII guard redacted ${redactedKeys.length} propert${redactedKeys.length === 1 ? "y" : "ies"}: ` +
            `${redactedKeys.join(", ")}. Do not pass PII to analytics — remove it at the call site.`,
        );
    }

    return sanitized;
};
